// On-focus build freshness check.
// Fetches the current index.html (cache-busted) whenever the tab becomes
// visible or regains focus, extracts the main /assets/*.js hash, and compares
// it to the hash that was loaded at startup. If it changed, a new build was
// deployed since this tab loaded — hard-reload to pick it up.
//
// No-ops in dev, in Lovable preview/iframe, and briefly after a reload so we
// don't ping the network on every tab switch.

import { isUploadInFlight } from "./uploadGuard";

const CHECK_MIN_INTERVAL_MS = 60_000; // don't check more than once a minute
const RELOAD_GUARD_KEY = "__freshness_reload_at__";
const RELOAD_GUARD_WINDOW_MS = 30_000;

function isPreviewOrDev(): boolean {
  if (!import.meta.env.PROD) return true;
  if (typeof window === "undefined") return true;
  try {
    if (window.top !== window.self) return true; // inside Lovable editor iframe
  } catch {
    return true;
  }
  const h = window.location.hostname;
  return (
    h.startsWith("id-preview--") ||
    h.startsWith("preview--") ||
    h.endsWith(".lovableproject.com") ||
    h.endsWith(".lovableproject-dev.com") ||
    h.endsWith(".beta.lovable.dev")
  );
}

function extractMainAssetHash(html: string): string | null {
  // Vite emits <script type="module" crossorigin src="/assets/index-XXXX.js">
  const m = html.match(/\/assets\/[A-Za-z0-9_-]+\.js/g);
  if (!m || m.length === 0) return null;
  // Sort so ordering differences don't cause false positives
  return m.slice().sort().join("|");
}

function currentAssetHash(): string | null {
  if (typeof document === "undefined") return null;
  const scripts = Array.from(document.querySelectorAll('script[src*="/assets/"]'));
  const srcs = scripts
    .map((s) => (s as HTMLScriptElement).src || "")
    .map((s) => {
      const i = s.indexOf("/assets/");
      return i >= 0 ? s.slice(i).split("?")[0] : "";
    })
    .filter(Boolean);
  if (srcs.length === 0) return null;
  return srcs.slice().sort().join("|");
}

let lastCheck = 0;
let checking = false;
let hiddenAt = 0;

const HIDDEN_MIN_MS = 30 * 60_000; // only consider reloading after 30+ min away

// Never blow away work in progress: any typed-in field, open dialog/sheet, or
// admin route with a form present blocks the reload.
function hasWorkInProgress(): boolean {
  if (typeof document === "undefined") return true;
  if (document.querySelector('[role="dialog"], [role="alertdialog"], [data-state="open"][data-radix-popper-content-wrapper]')) return true;
  const fields = Array.from(
    document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "input, textarea, select"
    )
  );
  return fields.some((el) => {
    if (el instanceof HTMLInputElement) {
      const t = el.type;
      if (t === "hidden" || t === "submit" || t === "button") return false;
      if (t === "checkbox" || t === "radio") return el.checked !== el.defaultChecked;
      if (t === "search") return false;
      return !!el.value;
    }
    if (el instanceof HTMLTextAreaElement) return !!el.value;
    return false;
  });
}

async function checkForNewBuild() {
  if (checking) return;
  // Never reload while a photo/receipt upload is running — returning from the
  // camera app fires a focus event, and a reload there would wipe the form.
  if (isUploadInFlight()) return;
  if (hasWorkInProgress()) return;
  const now = Date.now();
  if (now - lastCheck < CHECK_MIN_INTERVAL_MS) return;
  lastCheck = now;
  checking = true;
  try {
    const res = await fetch(`/index.html?_ts=${now}`, { cache: "no-store" });
    if (!res.ok) return;
    const html = await res.text();
    const latest = extractMainAssetHash(html);
    const loaded = currentAssetHash();
    if (!latest || !loaded) return;
    if (latest === loaded) return;

    // Guard against reload loops: if we already reloaded very recently, skip.
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || "0");
    if (now - last < RELOAD_GUARD_WINDOW_MS) return;
    if (hasWorkInProgress()) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    window.location.reload();
  } catch {
    // ignore network errors
  } finally {
    checking = false;
  }
}

export function startBuildFreshnessCheck() {
  if (isPreviewOrDev()) return;
  if (typeof window === "undefined") return;

  // Only check after the tab has been away for a long stretch — switching
  // windows to grab lead info should never reload and lose unsaved input.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    if (hiddenAt && Date.now() - hiddenAt >= HIDDEN_MIN_MS) void checkForNewBuild();
  });
  // Also check once on load (after a small delay so first paint isn't blocked)
  window.setTimeout(() => void checkForNewBuild(), 5_000);
}
